import { queryOptions, useQuery } from '@tanstack/react-query';
import { apiClient } from '../client';
import { systemKeys } from '../query-keys';
import { LOOKUP_LIST_CACHE } from '@/lib/query-client';
import { useAuthStore } from '@/stores/auth-store';
import {
  listSystemsContract,
  showSystemContract,
  type SystemWithCapacity,
  type ListSystemsResponse,
  type ShowSystemResponse
} from '@bliks-io/contracts';

/**
 * Fetch all systems list (no filtering)
 *
 * @example
 * ```tsx
 * function SystemsList() {
 *   const { data: systems, isLoading } = useSystems();
 *
 *   if (isLoading) return <Skeleton />;
 *   if (!systems) return null;
 *
 *   return <List items={systems} />;
 * }
 * ```
 */
export function useSystems() {
  return useSystemsQuery();
}

/**
 * Fetch all systems INCLUDING soft-deleted ones, for resolving entity refs on
 * old recordings (helpers, successors, assignees). Deleted systems carry a
 * `deletedAt` timestamp so read surfaces can gray them out.
 *
 * Never use this for pickers/selectors — those must only offer live systems
 * via {@link useSystems}.
 */
export function useSystemsForLookup(options?: { enabled?: boolean }) {
  const { token } = useAuthStore();

  return useQuery({
    ...systemsForLookupQueryOptions(),
    enabled: (options?.enabled ?? true) && !!token
  });
}

/**
 * Query options for the lookup variant, shared between the hook and route
 * loaders that prefetch the lookup (e.g. the process editor) so both sides
 * stay on the same cache key — and on the same `gcTime`, which only the
 * lookup variant lengthens.
 */
export function systemsForLookupQueryOptions() {
  return queryOptions({
    ...systemsQueryConfig(true),
    ...LOOKUP_LIST_CACHE
  });
}

function systemsQueryConfig(includeDeleted?: boolean) {
  return queryOptions({
    queryKey: systemKeys.list({
      filterBy: 'all',
      ...(includeDeleted ? { includeDeleted: true } : {})
    }),
    queryFn: async ({ signal }): Promise<SystemWithCapacity[]> => {
      const { data } = await apiClient.get<ListSystemsResponse>(
        includeDeleted
          ? `${listSystemsContract.path}?includeDeleted=true`
          : listSystemsContract.path,
        { signal }
      );
      return data.data;
    }
  });
}

function useSystemsQuery(includeDeleted?: boolean) {
  const { token } = useAuthStore();

  return useQuery({
    ...systemsQueryConfig(includeDeleted),
    enabled: !!token
  });
}

/**
 * Fetch systems where current user is the system owner
 *
 * Uses authenticated user from JWT token automatically.
 *
 * @example
 * ```tsx
 * function MySystemsList() {
 *   const { data: systems, isLoading } = useMySystems();
 *
 *   if (isLoading) return <Skeleton />;
 *   if (!systems) return null;
 *
 *   return <List items={systems} />;
 * }
 * ```
 */
export function useMySystems() {
  const { token } = useAuthStore();

  return useQuery({
    queryKey: systemKeys.list({ filterBy: 'owner' }),
    queryFn: async ({ signal }): Promise<SystemWithCapacity[]> => {
      const params = new URLSearchParams({
        filterBy: 'owner'
      });
      const { data } = await apiClient.get<ListSystemsResponse>(
        `${listSystemsContract.path}?${params.toString()}`,
        { signal }
      );
      return data.data;
    },
    enabled: !!token
  });
}

export const systemQueryOptions = (id: string) =>
  queryOptions({
    queryKey: systemKeys.detail(id),
    queryFn: async ({ signal }): Promise<ShowSystemResponse> => {
      // Detail pages must load soft-deleted systems too (direct URL,
      // links from old recordings); deleted ones carry deletedAt.
      const { data } = await apiClient.get<ShowSystemResponse>(
        `${showSystemContract.path.replace(':id', id)}?includeDeleted=true`,
        { signal }
      );
      return data;
    }
  });

/**
 * Fetch a single system by ID
 *
 * Returns system data including optional statistics (users, teams, roles, processes involved).
 *
 * @example
 * ```tsx
 * function SystemDetail({ id }: { id: string }) {
 *   const { data: system, isLoading } = useSystem(id);
 *
 *   if (isLoading) return <Skeleton />;
 *   if (!system) return <NotFound />;
 *
 *   return <DetailView system={system} />;
 * }
 * ```
 */
export function useSystem(id: string) {
  const { token } = useAuthStore();

  return useQuery({
    ...systemQueryOptions(id),
    enabled: !!token && !!id
  });
}
